import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Search, MapPin, User, Calendar as CalIcon, Hash, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLOR = {
  draft: 'text-[#A3A3A3]', scheduled: 'text-blue-400', in_progress: 'text-[#D4AF37]',
  pending_approval: 'text-amber-400', completed: 'text-green-400', cancelled: 'text-red-400',
};

/** Read-only schedule search usable by any active internal employee.
 *  Hits GET /api/admin/schedule/search (now open to view_schedule holders).
 *  onOpenResult(row) lets the parent open the job preview with normal permissions. */
const ScheduleSearchDialog = ({ onOpenResult, buttonClassName, testId = 'employee-schedule-search' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    if (!q.trim() && !dateFrom && !dateTo) { setResults([]); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      params.set('limit', '100');
      const { data } = await axios.get(`${API}/admin/schedule/search?${params.toString()}`, { withCredentials: true });
      setResults(data.results || []);
    } catch {
      setResults([]);
    }
    setLoading(false);
  }, [q, dateFrom, dateTo]);

  return (
    <>
      <Button
        data-testid={`${testId}-open`}
        onClick={() => setOpen(true)}
        variant="outline" size="sm"
        className={buttonClassName || 'border-[#262626] text-white hover:bg-[#262626] h-9'}
      >
        <Search className="w-4 h-4 mr-1.5" /> Search
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testId}-dialog`} className="bg-[#0F0F0F] border-[#262626] text-white max-w-2xl p-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5 pb-2">
            <DialogTitle className="flex items-center gap-2 text-white">
              <Search className="w-5 h-5 text-[#D4AF37]" /> Search Schedule
            </DialogTitle>
          </DialogHeader>

          <div className="px-5 pb-5 space-y-3 max-h-[78vh] overflow-y-auto">
            <form onSubmit={(e) => { e.preventDefault(); run(); }} className="space-y-2">
              <Input
                data-testid={`${testId}-input`}
                autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Customer, address, phone, WO#, PO#, technician, job #…"
                className="bg-[#0A0A0A] border-[#262626] text-white"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1 text-xs text-[#A3A3A3]"><CalIcon className="w-3.5 h-3.5" /> From</div>
                <Input data-testid={`${testId}-from`} type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white w-40 h-9" />
                <div className="flex items-center gap-1 text-xs text-[#A3A3A3]">To</div>
                <Input data-testid={`${testId}-to`} type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white w-40 h-9" />
                <Button data-testid={`${testId}-submit`} type="submit" disabled={loading} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-9">
                  {loading ? 'Searching…' : 'Search'}
                </Button>
                {(q || dateFrom || dateTo) && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setQ(''); setDateFrom(''); setDateTo(''); setResults(null); }} className="text-[#A3A3A3] h-9"><X className="w-4 h-4" /></Button>
                )}
              </div>
            </form>

            {results !== null && (
              <div data-testid={`${testId}-results`} className="space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-[#525252]">{results.length} result{results.length === 1 ? '' : 's'}</p>
                {results.length === 0 ? (
                  <p className="text-[#525252] text-sm py-4 text-center">No matching jobs. Try a customer name, address, WO#, or PO#.</p>
                ) : results.map((r) => (
                  <button
                    key={r.id}
                    data-testid={`${testId}-result-${r.id}`}
                    onClick={() => { onOpenResult?.(r); setOpen(false); }}
                    className="w-full text-left bg-[#141414] border border-[#262626] hover:border-[#D4AF37] rounded-md p-3 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-white font-medium">{r.customer_name || r.shift_title || 'Job'}</span>
                      <span className={`text-xs font-semibold ${STATUS_COLOR[r.status] || 'text-[#A3A3A3]'}`}>{(r.status || '').replace(/_/g, ' ')}</span>
                    </div>
                    <div className="mt-1 flex flex-col gap-0.5 text-xs text-[#A3A3A3]">
                      <span className="flex items-center gap-1"><CalIcon className="w-3 h-3" /> {r.date}{r.start_time ? ` · ${r.start_time}` : ''}{r.service_type ? ` · ${r.service_type}` : ''}</span>
                      {r.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.address}</span>}
                      {r.employee_name && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {r.employee_name}</span>}
                      {(r.work_order_number || r.office_supplied_po) && (
                        <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> {r.work_order_number ? `WO ${r.work_order_number}` : ''}{r.work_order_number && r.office_supplied_po ? ' · ' : ''}{r.office_supplied_po ? `PO ${r.office_supplied_po}` : ''}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScheduleSearchDialog;
